import { createReducer, on } from '@ngrx/store';
import * as FavoriteOccupationsActions from '../favorite-occupations/favorite-occupations.actions';
import * as UserProfileActions from '../user-profile/user-profile.actions';

import * as GoalActions from './goal.actions';
import * as GoalFeatureState from './goal.state';
import { VIA_GEN_STATUS } from '@cpd-app/shared-module/constants';

export const reducer = createReducer(
  GoalFeatureState.initialGoalState,

  on(GoalActions.resetGoalState, () => GoalFeatureState.initialGoalState),

  on(GoalActions.goalsLoadedSuccess, (state, { userGoals }) => {
    return { ...state, userGoals, hasLoaded: true };
  }),
  on(GoalActions.loadGoalsReportDetails, (state) => {
    return { ...state, loadingGoalsReport: true };
  }),
  on(GoalActions.loadGoalsReportDetailsSuccess, (state, { data }) => {
    // Apollo reports an empty response as `data: undefined`, and reading
    // through it here threw inside the reducer, which blanks every page that
    // renders while this runs (occupation and pathway among them). A missing
    // return link is worth degrading over, not white-screening.
    return {
      ...state,
      returnLink: data?.returnLink,
      dateGenerated: data?.generatedReportDate,
      loadingGoalsReport: false
    };
  }),
  on(
    GoalActions.goalsLoadedFailure,
    GoalActions.goalsUpdatedFailure,
    GoalActions.loadGoalsReportDetailsFailure,
    GoalActions.clearAllGoals,
    (state, action) => GoalFeatureState.initialGoalState
  ),
  on(GoalActions.goalsUpdatedSuccess, (state, { userGoals }) => {
    /*
     * GR.6. The write's answer is the goals as the server holds them, so they
     * are known now. Only when it carries goals: a refused write answers with
     * none, and "loaded, no career goal" would then let the Skills Survey
     * write a career goal over one the server still holds.
     */
    return { ...state, userGoals, hasLoaded: state.hasLoaded || !!userGoals };
  }),
  on(GoalActions.setReportDownloaded, (state, { reportDownloaded }) => {
    return { ...state, reportDownloaded };
  }),
  on(GoalActions.setReportShared, (state, { reportShared }) => {
    return { ...state, reportShared };
  }),
  on(GoalActions.setReportFile, (state, { reportFile }) => {
    return { ...state, reportFile };
  }),
  on(
    UserProfileActions.userSkillsSurveySavedSuccess,
    UserProfileActions.userInterestsSurveySavedSuccess,
    UserProfileActions.userWorkValuesSurveySavedSuccess,
    UserProfileActions.userLifestyleSurveySavedSuccess,

    GoalActions.setCareerGoal,
    GoalActions.setInstitutionGoal,
    GoalActions.setInstitutionProgramGoal,

    FavoriteOccupationsActions.favoriteOccupationAddedSuccess,
    FavoriteOccupationsActions.favoriteOccupationRemovedSuccess,
    (state) => ({
      ...state,
      reportFile: null
    })
  ),
  // L13. In flight from the press until the server answers, either way.
  on(GoalActions.refreshViaInsight, (state) => {
    return { ...state, refreshingAiGoalsReport: true, aiGoalsReportError: null };
  }),
  on(GoalActions.genAiGoalsReportSuccess, (state, { aiGoalsReport }) => {
    return {
      ...state,
      aiGoalsReport,
      aiGoalsReportError: null,
      refreshingAiGoalsReport: false
    };
  }),
  /*
   * V-2. genAiGoalsReportFailure was dispatched by TWO effects -- refreshViaInsight$
   * and genAiGoalsReport$ -- and handled by nothing. The action went into the
   * void: no error stored, no flag cleared. And LoaderFacade.showLoaderUntil
   * waits on the SUCCESS action only, so a failed refresh left the global
   * loader spinning too.
   */
  on(GoalActions.genAiGoalsReportFailure, (state, { error }) => {
    return {
      ...state,
      refreshingAiGoalsReport: false,
      aiGoalsReportError:
        typeof error === 'string'
          ? error
          : (error?.message ?? 'Via could not finish this report.')
    };
  }),
  on(
    GoalActions.setLearningSuggestionsFromAiChatSuccess,
    (state, { aiDeepInsights }) => {
      return {
        ...state,
        aiDeepInsights
      };
    }
  ),
  on(GoalActions.loadAiDeepInsight, (state) => {
    return { ...state, loadingAiDeepInsight: true };
  }),
  on(GoalActions.loadAiDeepInsightSuccess, (state, { aiDeepInsights }) => {
    return {
      ...state,
      aiDeepInsights,
      loadingAiDeepInsight: false
    };
  }),
  on(GoalActions.loadAiDeepInsightFailure, (state) => {
    return { ...state, loadingAiDeepInsight: false };
  }),
  on(GoalActions.connectGenStatus, (state) => {
    return { ...state, statusConnect: true };
  }),
  on(GoalActions.disConnectGenStatus, (state) => {
    return { ...state, statusConnect: false };
  }),
  on(GoalActions.connectGenStatusFailure, (state) => {
    return { ...state, statusConnect: false };
  }),
  /**
   * WHY "Insights from Via" SPUN FOREVER EVEN THOUGH THE FAILED PANEL EXISTS.
   *
   * The stream did deliver `failed`, and connectGenStatus$ did act on it -- by
   * disconnecting and dispatching nothing else. So the ONLY copy of the status
   * the panel reads, `aiGoalsReport.status`, kept whatever the page's own
   * initial read had put there: `queued` or `in-progress`. isShowSkeleton$ is
   * true for exactly those two, so the skeleton stayed up for a report that had
   * been dead in the database for minutes.
   *
   * Writing the status here, rather than re-fetching, is the point: the stream
   * has just told us the answer, and the alternative dispatch available
   * (genAiGoalsReport) is a mutation that would start another generation.
   */
  on(GoalActions.genStatusReportFailed, (state) => {
    if (!state.aiGoalsReport) {
      return state;
    }
    return {
      ...state,
      aiGoalsReport: {
        ...state.aiGoalsReport,
        status: VIA_GEN_STATUS.failed
      }
    };
  })
);
